/**
 * Fleet Energy Bar — compact stacked horizontal bar card (Tier 1).
 * Sources bar: PV + Grid Import + curtailed ghost.
 * Destinations bar: Load + Battery Charging + Grid Export.
 * Data source: get_analytics_fleet_summary RPC (server-side rollup, org-scoped).
 * Click → navigates to /app/analytics#energy-breakdown.
 */

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '@/contexts/AppContext';
import { Loader2, ArrowRight } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useFleetAnalytics } from '@/hooks/useFleetAnalytics';

const COLORS = {
  pv: '#BEA504',          // solar-gold
  gridImport: '#60A5FA',  // blue
  load: '#60A5FA',        // blue
  battery: '#BEA504',     // solar-gold
  gridExport: '#10B981',  // green-primary
  curtailed: '#D97706',   // amber-primary
};

interface BarSegment {
  key: string;
  label: string;
  value: number;
  color: string;
  ghost?: boolean;
}

function StackedBar({ segments, totalLabel }: { segments: BarSegment[]; totalLabel: string }) {
  const total = segments.reduce((s, seg) => s + seg.value, 0);
  if (total === 0) return null;

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {segments.filter(s => !s.ghost && s.value > 0).map((seg) => (
            <span key={seg.key} className="flex items-center gap-1 text-[10px] text-muted-foreground">
              <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: seg.color }} />
              {seg.label}
            </span>
          ))}
          {segments.some(s => s.ghost && s.value > 0) && (
            <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
              <span className="h-1.5 w-1.5 rounded-full opacity-40" style={{ backgroundColor: COLORS.curtailed }} />
              Curtailed (estimated)
            </span>
          )}
        </div>
        <span className="text-xs text-muted-foreground font-mono">{totalLabel}</span>
      </div>
      <TooltipProvider delayDuration={100}>
        <div className="flex h-7 rounded-md overflow-hidden bg-muted/30">
          {segments.map((seg) => {
            if (seg.value <= 0) return null;
            const pct = (seg.value / total) * 100;
            const showLabel = pct > 15;
            return (
              <Tooltip key={seg.key}>
                <TooltipTrigger asChild>
                  <div
                    className="h-full flex items-center justify-center transition-all"
                    style={{
                      width: `${pct}%`,
                      backgroundColor: seg.color,
                      opacity: seg.ghost ? 0.3 : 1,
                      minWidth: seg.value > 0 ? '2px' : 0,
                    }}
                  >
                    {showLabel && (
                      <span className="text-[10px] font-mono text-white/90 truncate px-1">
                        {seg.value >= 1000
                          ? `${(seg.value / 1000).toFixed(1)}k`
                          : seg.value.toFixed(0)}
                      </span>
                    )}
                  </div>
                </TooltipTrigger>
                <TooltipContent className="bg-wt-bg-primary border-border text-xs font-mono">
                  {seg.label}{seg.ghost ? ' (estimated)' : ''}: {seg.value.toLocaleString(undefined, { maximumFractionDigits: 1 })} kWh ({pct.toFixed(1)}%)
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>
      </TooltipProvider>
    </div>
  );
}

export function FleetEnergyBar() {
  const navigate = useNavigate();
  const { organization, overrideOrgId } = useApp();
  const orgId = overrideOrgId ?? organization?.id;
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const { data: fa, isLoading } = useFleetAnalytics(orgId, today, today);

  if (isLoading) {
    return (
      <div className="flex-1 min-w-[280px] rounded-lg border border-border bg-card p-5 flex items-center justify-center min-h-[180px]">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!fa || fa.property_count === 0) {
    return (
      <div className="flex-1 min-w-[280px] rounded-lg border border-border bg-card p-5 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Energy Today (so far)</span>
        </div>
        <div className="flex items-center justify-center min-h-[120px]">
          <div className="text-center space-y-1">
            <p className="font-mono text-[20px] font-semibold text-foreground">--</p>
            <p className="text-xs text-muted-foreground">No generation today</p>
          </div>
        </div>
      </div>
    );
  }

  const pvKwh = fa.fleet_pv_kwh;
  const loadKwh = fa.fleet_load_kwh;
  const batteryChargedKwh = fa.fleet_battery_charged_kwh;
  const gridExportKwh = fa.fleet_grid_export_kwh;
  const gridImportKwh = fa.fleet_grid_import_kwh;
  const curtailedKwh = fa.fleet_curtailed_kwh;

  const totalSources = pvKwh + gridImportKwh;
  const totalPotential = pvKwh + curtailedKwh;
  const totalDestinations = loadKwh + batteryChargedKwh + gridExportKwh;
  const hasCurtailment = curtailedKwh > 0;

  const sourceSegments: BarSegment[] = [
    { key: 'pv', label: 'PV', value: pvKwh, color: COLORS.pv },
    { key: 'grid', label: 'Grid', value: gridImportKwh, color: COLORS.gridImport },
    { key: 'curtailed', label: 'Curtailed', value: curtailedKwh, color: COLORS.curtailed, ghost: true },
  ];

  const destSegments: BarSegment[] = [
    { key: 'load', label: 'Load', value: loadKwh, color: COLORS.load },
    { key: 'battery', label: 'Battery', value: batteryChargedKwh, color: COLORS.battery },
    { key: 'export', label: 'Export', value: gridExportKwh, color: COLORS.gridExport },
  ];

  // Curtailment dollar disclosure (WT-402): value comes from the RPC's per-property
  // resolved rates; disclose how many of the curtailing properties fall back to a
  // regional default rate so the figure is never presented as a measured/known number.
  const wastedDollars = fa.fleet_total_curtailment_dollars;
  const curtProps = (fa.properties ?? []).filter((p) => (p.curtailed_kwh ?? 0) > 0);
  const defaultRateCount = curtProps.filter((p) => p.rate_source_resolved === 'default').length;
  const rateDisclosure =
    defaultRateCount > 0
      ? `${defaultRateCount} of ${curtProps.length} properties use a regional default rate (no utility rate configured)`
      : undefined;

  return (
    <div
      className="flex-1 min-w-[280px] rounded-lg border border-border bg-card p-5 space-y-3 cursor-pointer transition-colors hover:border-muted-foreground/30"
      role="button"
      tabIndex={0}
      onClick={() => navigate('/app/analytics#energy-breakdown')}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          navigate('/app/analytics#energy-breakdown');
        }
      }}
    >
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">Energy Today (so far)</span>
        <button
          className="flex items-center gap-1 text-[11px] text-primary hover:underline font-mono"
          onClick={(e) => {
            e.stopPropagation();
            navigate('/app/analytics#energy-breakdown');
          }}
        >
          View Details
          <ArrowRight className="h-3 w-3" />
        </button>
      </div>

      {/* Sources bar */}
      <div>
        <p className="text-xs font-medium text-foreground mb-1">
          SOURCES <span className="font-mono text-muted-foreground ml-1">{totalSources.toLocaleString(undefined, { maximumFractionDigits: 0 })} kWh</span>
        </p>
        <StackedBar
          segments={sourceSegments}
          totalLabel={hasCurtailment ? `${totalPotential.toLocaleString(undefined, { maximumFractionDigits: 0 })} kWh potential` : ''}
        />
      </div>

      {/* Destinations bar */}
      <div>
        <p className="text-xs font-medium text-foreground mb-1">
          USED <span className="font-mono text-muted-foreground ml-1">{totalDestinations.toLocaleString(undefined, { maximumFractionDigits: 0 })} kWh</span>
        </p>
        <StackedBar segments={destSegments} totalLabel="" />
      </div>

      {/* Curtailment callout */}
      {hasCurtailment && (
        <div className="space-y-0.5">
          <p className="text-[11px] text-wt-amber-primary font-mono" title={rateDisclosure}>
            {curtailedKwh.toLocaleString(undefined, { maximumFractionDigits: 1 })} kWh curtailed (estimated)
            {wastedDollars >= 1 && ` · ~$${wastedDollars.toFixed(0)} estimated`}
          </p>
          {rateDisclosure && (
            <p className="text-[10px] text-muted-foreground">{rateDisclosure}</p>
          )}
        </div>
      )}
    </div>
  );
}
